import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useFocusTrap } from '../../../hooks/use-focus-trap'
import { useIsAppLocked } from '../../../stores/app-lock-store'

/**
 * Accessible modal for the recording flows: `role="dialog"` (or
 * `alertdialog` for destructive confirms), labelled by its title, focus
 * trapped, Esc closes (unless `busy`), focus returns to the opener.
 */
export function RecordingDialog({
  title,
  description,
  onClose,
  busy = false,
  alert = false,
  size = 'md',
  children,
  footer,
}: {
  title: string
  description?: ReactNode
  onClose: () => void
  busy?: boolean
  alert?: boolean
  size?: 'md' | 'lg'
  children?: ReactNode
  footer?: ReactNode
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const busyRef = useRef(busy)
  busyRef.current = busy
  const lockedRef = useRef(false)

  // Portalled outside the soft-lock gate, so step aside while the app is locked.
  const locked = useIsAppLocked()
  useFocusTrap(containerRef, !locked)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busyRef.current && !lockedRef.current) {
        event.stopPropagation()
        onCloseRef.current()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  lockedRef.current = locked
  if (locked) return null

  // Portalled: the REC bar lives in a low fixed stacking context, and its
  // dialogs must still sit above the app chrome.
  return createPortal(
    <div className="pointer-events-auto fixed inset-0 z-[55] flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4">
      <div
        ref={containerRef}
        role={alert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={`flex max-h-[calc(100dvh-var(--safe-top))] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-h-[90dvh] sm:rounded-2xl ${
          size === 'lg' ? 'sm:max-w-xl' : 'sm:max-w-md'
        }`}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 pb-4">
          <h2 id={titleId} className="text-lg font-bold text-slate-900">
            {title}
          </h2>
          {description && (
            <div id={descriptionId} className="mt-1 text-sm text-slate-600">
              {description}
            </div>
          )}
          {children}
        </div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 px-5 pt-3 pb-[max(0.75rem,var(--safe-bottom))] sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export const dialogPrimaryButton =
  'a11y-touch-target inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50'
export const dialogDangerButton =
  'a11y-touch-target inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50'
export const dialogSecondaryButton =
  'a11y-touch-target inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50'
